/**
 * Nested Prompts System
 *
 * Manages conversation memory for AI chats: sliding window, summarization of
 * older messages, truncation of long content, and inclusion of script/context
 * in the system prompt (with limits to avoid exceeding model context).
 */

import type { AIMessage } from './ai-service';
import type { ResultsExplanationContext } from './ai-prompts';

/** Number of most recent messages to send in full (rest are summarized). ~6–7 exchanges. */
export const MAX_RECENT_MESSAGES = 14;

/** Max lines of script code to include in system prompt (avoids token overflow). */
export const MAX_SCRIPT_LINES_IN_PROMPT = 200;

/** Max characters per single message in the recent window (truncate if longer). */
export const MAX_MESSAGE_CHARS = 6000;

/** Approximate max chars for the full system prompt (leave room for messages + response). */
export const MAX_SYSTEM_PROMPT_CHARS = 28000;

export interface NestedPromptContext {
  level: number; // Depth level in the prompt hierarchy
  parentId?: string; // ID of parent context
  contextId: string; // Unique ID for this context
  summary: string; // Condensed summary of this context
  fullContext?: ResultsExplanationContext; // Full context (only at root level)
  metadata?: {
    intent?: string;
    timestamp: number;
    messageCount: number;
  };
}

export interface PromptNode {
  id: string;
  parentId?: string;
  level: number;
  summary: string;
  fullContent?: string;
  children: string[]; // IDs of child nodes
  createdAt: number;
}

/**
 * Build nested prompt structure
 * Creates a hierarchical context tree for efficient prompt management
 */
export function buildNestedPrompts(
  baseContext: ResultsExplanationContext,
  conversationHistory: AIMessage[],
  _maxLevels: number = 3
): {
  rootContext: NestedPromptContext;
  conversationSummary: string;
  messages: AIMessage[];
} {
  // Create root context with full information
  const rootContext: NestedPromptContext = {
    level: 0,
    contextId: 'root',
    summary: buildContextSummary(baseContext),
    fullContext: baseContext,
    metadata: {
      timestamp: Date.now(),
      messageCount: conversationHistory.length,
    },
  };

  // Sliding window: keep last N messages, summarize the rest
  const recentMessages = conversationHistory.slice(-MAX_RECENT_MESSAGES);
  const olderMessages =
    conversationHistory.length > MAX_RECENT_MESSAGES
      ? conversationHistory.slice(0, -MAX_RECENT_MESSAGES)
      : [];

  let conversationSummary = '';
  if (olderMessages.length > 0) {
    conversationSummary = `Previous conversation (${olderMessages.length} messages): `;
    conversationSummary += summarizeMessages(olderMessages);
  }

  // Truncate very long messages to stay within context budget
  const truncatedRecent = recentMessages.map((msg) => ({
    ...msg,
    content: truncateMessage(msg.content, MAX_MESSAGE_CHARS),
  }));

  return {
    rootContext,
    conversationSummary,
    messages: truncatedRecent,
  };
}

/**
 * Truncate a single message if it exceeds the character limit, keeping start and end.
 */
function truncateMessage(content: string, maxChars: number): string {
  if (!content || content.length <= maxChars) return content;
  const head = Math.floor(maxChars * 0.65);
  const tail = Math.floor(maxChars * 0.2);
  return (
    content.slice(0, head) +
    '\n\n[... message truncated for context ...]\n\n' +
    content.slice(-tail)
  );
}

/**
 * Summarize older messages so the model retains continuity without sending full text.
 */
function summarizeMessages(messages: AIMessage[]): string {
  if (messages.length === 0) return '';

  const userMessages = messages.filter((m) => m.role === 'user').map((m) => m.content);
  const assistantMessages = messages.filter((m) => m.role === 'assistant').map((m) => m.content);

  let summary = '';
  if (userMessages.length > 0) {
    const preview = userMessages.slice(0, 4).map((c) => (c.length > 80 ? c.slice(0, 80) + '…' : c));
    summary += `User topics: ${preview.join('; ')}`;
    if (userMessages.length > 4) summary += ` (+${userMessages.length - 4} more)`;
  }
  if (assistantMessages.length > 0) {
    summary += '. Assistant gave analysis and code suggestions.';
    const lastAssistant = assistantMessages[assistantMessages.length - 1];
    if (lastAssistant && lastAssistant.length > 0 && lastAssistant.length <= 200) {
      summary += ` Last reply: "${lastAssistant.slice(0, 200)}${lastAssistant.length > 200 ? '…' : ''}"`;
    } else if (lastAssistant && lastAssistant.length > 200) {
      summary += ` Last reply (summary): "${lastAssistant.slice(0, 120)}…"`;
    }
  }
  return summary;
}

/**
 * Build context summary for nested prompts
 */
function buildContextSummary(context: ResultsExplanationContext): string {
  let summary = '';

  if (context.scriptCode) {
    const lines = context.scriptCode.split('\n').length;
    summary += `DSL script (${lines} lines) analyzing `;
  }

  if (context.datasetInfo?.symbol) {
    summary += `${context.datasetInfo.symbol} `;
  }

  if (context.datasetInfo?.timeframe) {
    summary += `(${context.datasetInfo.timeframe}) `;
  }

  if (context.resultType) {
    summary += `with ${context.resultType} results.`;
  }

  return summary.trim() || 'Analysis context available.';
}

/**
 * Truncate script to a maximum number of lines for inclusion in the system prompt.
 */
function truncateScriptForPrompt(scriptCode: string, maxLines: number): string {
  const lines = scriptCode.split('\n');
  if (lines.length <= maxLines) return scriptCode;
  const kept = lines.slice(0, maxLines).join('\n');
  const remaining = lines.length - maxLines;
  return `${kept}\n\n# ... (${remaining} more lines not shown for context limit)`;
}

/**
 * Create optimized prompt with nested structure and memory limits.
 * Includes script in context when present (truncated), conversation summary,
 * and caps total system prompt size to avoid exceeding model context.
 */
export function createNestedPrompt(
  nestedContext: {
    rootContext: NestedPromptContext;
    conversationSummary: string;
    messages: AIMessage[];
  },
  systemPrompt: string,
  currentMessage: string
): AIMessage[] {
  const { rootContext, conversationSummary, messages } = nestedContext;

  let optimizedSystemPrompt = systemPrompt;

  if (rootContext.fullContext) {
    optimizedSystemPrompt += `\n\n## CONTEXT SUMMARY:\n${rootContext.summary}`;

    if (conversationSummary) {
      optimizedSystemPrompt += `\n\n## CONVERSATION HISTORY:\n${conversationSummary}`;
    }

    if (rootContext.fullContext.scriptCode) {
      const scriptExcerpt = truncateScriptForPrompt(
        rootContext.fullContext.scriptCode,
        MAX_SCRIPT_LINES_IN_PROMPT
      );
      optimizedSystemPrompt += `\n\n## CURRENT SCRIPT (for reference when editing or explaining):\n\`\`\`dsl\n${scriptExcerpt}\n\`\`\``;
      optimizedSystemPrompt +=
        '\n\nWhen suggesting code changes, follow the DSL syntax rules from the system prompt above (config, input, signal, stats by, output).';
    }

    optimizedSystemPrompt +=
      '\n\n**Note**: Use the context above to answer accurately. When modifying code, output the full updated script when asked.';
  }

  if (optimizedSystemPrompt.length > MAX_SYSTEM_PROMPT_CHARS) {
    optimizedSystemPrompt =
      optimizedSystemPrompt.slice(0, MAX_SYSTEM_PROMPT_CHARS - 80) +
      '\n\n[... system context truncated to fit model context window ...]';
  }

  const promptMessages: AIMessage[] = [
    { role: 'system', content: optimizedSystemPrompt },
    ...messages,
    { role: 'user', content: truncateMessage(currentMessage, MAX_MESSAGE_CHARS) },
  ];

  return promptMessages;
}

/**
 * Extract key information from context for nested prompts
 */
export function extractContextKeys(context: ResultsExplanationContext): {
  hasScript: boolean;
  hasResults: boolean;
  hasDataset: boolean;
  resultType?: string;
} {
  return {
    hasScript: !!context.scriptCode,
    hasResults: !!context.results && Object.keys(context.results).length > 0,
    hasDataset: !!context.datasetInfo,
    resultType: context.resultType,
  };
}
